import React, { useState, useEffect, useRef, useCallback } from 'react';
import { scaleLinear, scaleOrdinal } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { LegendOrdinal } from '@visx/legend';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { localPoint } from '@visx/event';
import { extent, bisector } from 'd3-array';
import PropTypes from 'prop-types';

import {
  getParams,
  thousandFormat,
  financialMonth,
} from '../../../../Utils/Functions';
import {
  months,
  enMonths,
  activeLabelColor,
} from '../../../../Assets/js/constant';

const xSelector = (d) => d.Month;
const ySelector = (d) => d.DebtorDays;
const bisectDate = bisector(xSelector).left;

const grayColors = [
  '#5e5e5e',
  '#828282',
  '#9f9d9d',
  '#b7b7b7',
  '#d0cdcd',
  '#E8E8E8',
  '#F5F5F5',
];

const DebtorsDays = ({
  summaryData,
  selectedYears,
  selectedMonths,
  defaultStartMonth,
  handleFilter,
  target,
}) => {
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [data, setData] = useState({});
  const [lineStrokeSet, setLineStrokeSet] = useState({});
  const [lineClicked, setLineClicked] = useState(false);

  const isMountedRef = useRef(true);

  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
    }
  }, []);

  const generateLineColors = useCallback(() => {
    let lineStrokeSetObj = {};

    if (selectedYears.length > 0) {
      for (let i = 0; i < selectedYears.length; i++) {
        lineStrokeSetObj[selectedYears[i]] = grayColors[i];
      }
    }

    setLineStrokeSet(lineStrokeSetObj);
  }, [selectedYears]);

  const prepareData = useCallback(() => {
    const dataObj = {};

    summaryData.forEach((d) => {
      if (!dataObj[d.Year])
        dataObj[d.Year] = [
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
        ];
      let commonMonth = financialMonth(d.Month, defaultStartMonth);
      const _d = {
        ...d,
        Month: commonMonth,
      };

      if (d.DebtorDays && selectedYears.indexOf(d.Year) > -1)
        dataObj[_d.Year][_d.Month - 1] = _d;
    });

    Object.keys(dataObj).map((key) => {
      for (let i = 0; i < dataObj[key].length; i++) {
        if (!dataObj[key][i]) {
          dataObj[key].splice(i, 1);
          i--;
        }
      }
      return [];
    });

    setData(dataObj);
  }, [summaryData, selectedYears, defaultStartMonth]);

  const showTooltipHandler = useCallback((event, dataArray, xScale, yScale) => {
    const { x } = localPoint(event);
    const x0 = xScale.invert(x);
    const index = bisectDate(dataArray, x0, 1);
    const d0 = dataArray[index - 1];
    const d1 = dataArray[index];
    let d = d0;
    if (d1 && d1.date) {
      d = x0 - xSelector(d0) > xSelector(d1) - x0 ? d1 : d0;
    }

    showTooltip({
      tooltipData: d,
      tooltipLeft: xScale(xSelector(d)),
      tooltipTop: yScale(ySelector(d)),
    });

    setLineClicked(false);
  }, [showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  const handleLineSelect = useCallback((event, dataArray) => {
    setLineClicked(prev => !prev);
  }, []);

  const handleLabelSelect = useCallback((event, month) => {
    let _selectedMonths;

    let index = -1;
    for (let i = 0; i < selectedMonths.length; i++) {
      if (selectedMonths[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedMonths = selectedMonths.slice();

      if (index === -1) {
        _selectedMonths.push(month);
      } else {
        _selectedMonths.splice(index, 1);
      }
    } else {
      if (index !== -1 && selectedMonths.length === 1) {
        _selectedMonths = [];
      } else {
        _selectedMonths = [month];
      }
    }

    handleFilter({
      selectedMonths: _selectedMonths,
    });
  }, [selectedMonths, handleFilter]);

  const deSelectAll = useCallback(() => {
    handleFilter({
      selectedMonths: [],
    });
  }, [handleFilter]);

  // Effects
  useEffect(() => {
    isMountedRef.current = true;
    generateLineColors();
    prepareData();
    window.addEventListener('resize', onResize);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [onResize, generateLineColors, prepareData]);

  useEffect(() => {
    if (isMountedRef.current) {
      generateLineColors();
      prepareData();
    }
  }, [summaryData, defaultStartMonth, selectedYears, generateLineColors, prepareData]);

  // Render logic
  const [minValue, maxValue] = extent(summaryData, ySelector);

  let xTransform = 0;
  let maxYValue = maxValue;
  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (parseInt(thousandFormat(maxYValue)[0]) > 7)
      xTransform = 7.5 * (thousandFormat(maxYValue).length + 1) + 10;
    else xTransform = 7.5 * thousandFormat(maxYValue).length + 10;
  }

  const width = window.innerWidth - 16;
  const height = window.innerHeight / 6;
  const margin = {
    top: 10,
    right: 10,
    bottom: 20,
    left: xTransform,
  };

  const { xMax, yMax } = getParams(window, width, height, margin);

  // scales
  const xScale = scaleLinear({
    domain: [1, 12],
    range: [xMax / 24, xMax - width / 24],
    nice: true,
  });
  const xAxisScale = scaleLinear({
    domain: [0, 12],
    range: [0, xMax],
  });

  const yScale = scaleLinear({
    domain: [
      minValue > 0 ? 0 : minValue,
      maxValue > target ? maxValue : target,
    ],
    range: [yMax, 0],
    nice: true,
  });

  const colorRange = selectedYears.reduce((ret, year) => {
    ret.push(lineStrokeSet[parseInt(year)]);
    return ret;
  }, []);
  const colors = scaleOrdinal({
    domain: selectedYears,
    range: colorRange,
  });

  let tooltipTimeout;
  let data_length = 0;
  Object.keys(data).forEach((fy) => (data_length += data[fy].length));

  return (
    <div id="debtors-top-chart">
      <div
        style={{
          position: 'absolute',
          top: margin.top,
          right: margin.right,
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          fontSize: 12,
        }}
      >
        <LegendOrdinal
          scale={colors}
          direction="row"
          shapeMargin="0 8px"
          labelMargin="0 0 0 0"
        />
      </div>
      
      <div className="subtitle mb-10">Days Outstanding</div>

      <svg width={width} height={height}>
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="transparent"
          onClick={deSelectAll}
        />

        <Group top={margin.top} left={margin.left}>
          <AxisLeft
            tickStroke="black"
            tickLabelProps={(value, index) => ({
              fill: 'black',
              fontSize: 11,
              textAnchor: 'end',
              dy: '0.33em',
              dx: '-0.2em',
            })}
            stroke="black"
            scale={yScale}
            numTicks={4}
            hideZero
          />

          {target && (
            <LinePath
              data={[
                { x: 0, y: yScale(target) ? yScale(target) : 0 },
                { x: xMax, y: yScale(target) ? yScale(target) : 0 },
              ]}
              x={(d) => (d.x ? d.x : 0)}
              y={(d) => (d.y ? d.y : 0)}
              stroke="#222"
              strokeDasharray="8,4"
            />
          )}

          {Object.keys(data).map((key) => {
            const lineStroke = lineStrokeSet[key];

            return (
              <LinePath
                key={key}
                data={data[key]}
                x={(d) => (xScale(xSelector(d)) ? xScale(xSelector(d)) : 0)}
                y={(d) => (yScale(ySelector(d)) ? yScale(ySelector(d)) : 0)}
                strokeWidth={data_length === 1 ? 6 : 3}
                stroke={lineStroke}
                strokeLinecap="round"
                fill="transparent"
                onClick={(event) => {
                  handleLineSelect(event, data[key]);
                }}
                onMouseMove={(event) => {
                  if (tooltipTimeout) clearTimeout(tooltipTimeout);
                  showTooltipHandler(event, data[key], xScale, yScale);
                }}
                onMouseLeave={(event) => {
                  tooltipTimeout = setTimeout(() => {
                    hideTooltipHandler();
                  }, 300);
                }}
                onTouchMove={(event) => {
                  if (tooltipTimeout) clearTimeout(tooltipTimeout);
                  showTooltipHandler(event, data[key], xScale, yScale);
                }}
                onTouchEnd={(event) => {
                  tooltipTimeout = setTimeout(() => {
                    hideTooltipHandler();
                  }, 300);
                }}
              />
            );
          })}

          {months &&
            months.map((month, index) => {
              return (
                <rect
                  key={index}
                  x={(index * xMax) / 12}
                  y={yMax ? yMax : 0}
                  width={xMax / 12}
                  height={margin.bottom}
                  fill="transparent"
                  onClick={(event) =>
                    handleLabelSelect(
                      event,
                      financialMonth(month, defaultStartMonth),
                    )
                  }
                />
              );
            })}

          <AxisBottom
            top={yMax ? yMax : 0}
            left={0}
            scale={xAxisScale ? xAxisScale : 0}
            hideZero
            numTicks={13}
            stroke="black"
            tickStroke="black"
            tickLabelProps={(value, index) => ({
              textAnchor: 'middle',
              fontSize: 11,
              fontFamily: 'Arial',
              dx: `${xMax ? -xMax / 24 : 0}`,
              dy: '-0.5em',
            })}
            tickComponent={({ formattedValue, ...tickProps }) => (
              <text
                {...tickProps}
                style={{
                  fontWeight:
                    selectedMonths.indexOf(
                      financialMonth(
                        formattedValue,
                        defaultStartMonth,
                      ),
                    ) > -1
                      ? 'bold'
                      : 500,
                }}
                fill={
                  selectedMonths.indexOf(
                    financialMonth(
                      formattedValue,
                      defaultStartMonth,
                    ),
                  ) > -1
                    ? activeLabelColor
                    : 'black'
                }
                onClick={(event) =>
                  handleLabelSelect(
                    event,
                    financialMonth(
                      formattedValue,
                      defaultStartMonth,
                    ),
                  )
                }
              >
                {
                  enMonths[
                    financialMonth(
                      formattedValue,
                      defaultStartMonth,
                    ) - 1
                  ]
                }
              </text>
            )}
          />

          {tooltipOpen && (
            <g>
              <circle
                cx={tooltipLeft}
                cy={tooltipTop}
                r={lineClicked ? 8 : 6}
                fill={lineStrokeSet[tooltipData.Year]}
                stroke="#e3e3e3"
                strokeWidth={lineClicked ? 3 : 0}
                style={{ pointerEvents: 'none' }}
              />
            </g>
          )}
        </Group>
      </svg>

      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop}
          left={tooltipLeft - 24}
        >
          <div className="bkg-white color-black" style={{ fontSize: 12, width: 110 }}>
            <p className="tooltipPanel">
              <span style={{ color: 'gray' }}>Month:</span>
              <span style={{ float: 'right', fontWeight: 600, color: 'black' }}>{`${
                enMonths[
                  financialMonth(
                    xSelector(tooltipData),
                    defaultStartMonth,
                  ) - 1
                ]
              }`}</span>
            </p>
            <p className="tooltipPanel">
              <span style={{ color: 'gray' }}>DebtorDays:</span>
              <span style={{ float: 'right', fontWeight: 600, color: 'black' }}>{`${thousandFormat(
                ySelector(tooltipData),
              )}`}</span>
            </p>
          </div>
        </TooltipWithBounds>
      )}
    </div>
  );
};

DebtorsDays.propTypes = {
  summaryData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
  target: PropTypes.number,
};

export default DebtorsDays;
